import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Box, CircularProgress, Grid } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { yupResolver } from '@hookform/resolvers/yup';
import { jwtDecode } from 'jwt-decode';

import { useRegister } from '../../api-calls/auth.queries';
import { useAuth } from '../../context/auth';
import { routes } from '../../constants';
import { register as registerValidation } from '../../validation/schemas';
import Button from '../../components/Button';
import * as T from '../../components/Typography';
import Error from '../../components/Error';
import colors from '../../theme/colors';
import rocket from '../../components/assets/rocket.svg';

import { ControllerTextField } from '../../components/Inputs/Textfield';
import InvalidToken from '../../components/InvalidToken';

interface RegisterFormInputs {
  password: string;
  confirmPassword: string;
}

interface DecodedToken {
  leadId: string;
  email: string;
  firstName: string;
  lastName: string;
  job: string;
  organisationName: string;
  exp: number;
}

const Register: React.FC = () => {
  const [loading, setLoading] = useState<boolean>(true);
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isDuplicateError, setIsDuplicateError] = useState<boolean>(false);

  const { control, handleSubmit } = useForm<RegisterFormInputs>({
    resolver: yupResolver(registerValidation)
  });

  const navigate = useNavigate();
  const { logout } = useAuth();

  const { mutateAsync, isPending } = useRegister();

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const jwtToken = urlParams.get('token');

    if (jwtToken) {
      try {
        const decodedToken: DecodedToken = jwtDecode(jwtToken);
        const currentTime = Date.now() / 1000;

        if (
          decodedToken.exp > currentTime &&
          decodedToken.leadId &&
          decodedToken.email
        ) {
          setToken(jwtToken);
        }
      } catch {
        // malformed token — leave `token` empty, falls through to <InvalidToken />
      }
    }
    setLoading(false);
  }, []);

  const onSubmit = async (data: RegisterFormInputs) => {
    const { password } = data;

    try {
      await mutateAsync({ token, password });
      logout();
      navigate(routes.LOGIN);
    } catch (err: any) {
      if (err.response?.status === 409) {
        setIsDuplicateError(true);
      }
      setError('Failed to register.');
    }
  };

  if (loading) {
    return (
      <Box
        width={'100%'}
        display={'flex'}
        alignItems={'center'}
        justifyContent={'center'}
      >
        <CircularProgress color={'secondary'} />
      </Box>
    );
  }

  if (isDuplicateError) {
    return (
      <Error
        errorTitle="Duplicate email"
        errorMsg="Sorry, this email is already registered. Please log in instead."
        buttonLabel="Log in"
        buttonOnClick={() => navigate(routes.LOGIN)}
      />
    );
  }

  if (error) {
    return <Error />;
  }

  if (!token) {
    return (
      <InvalidToken
        title={'The link you clicked is invalid or has expired.'}
        subtitle={
          "Resend the link by clicking below. If that doesn't work, please contact support."
        }
      />
    );
  }

  return (
    <Grid
      container
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
    >
      <Grid
        container
        maxWidth={'384px'}
        flexDirection="column"
        alignItems="center"
        justifyContent="center"
      >
        <img src={rocket} alt="account verified" />
        <T.H4
          mt="6"
          mb="2"
          ta="center"
          color={colors.neutral.shades.shade700}
        >
          EMAIL VERIFIED SUCCESSFULLY!
        </T.H4>
        <T.TextSmallRegular
          ta="center"
          mb={6}
          center
          color={colors.neutral.shades.shade600}
        >
          You can now create your Index portal account.
          <br />
          First, choose a strong password.
        </T.TextSmallRegular>
      </Grid>
      <form
        onSubmit={handleSubmit(onSubmit)}
        style={{ width: '100%', maxWidth: '384px' }}
      >
        <Grid item>
          <ControllerTextField
            control={control}
            label="Your password"
            name="password"
            placeholder="Password"
            type="password"
          />
        </Grid>
        <Grid item mt={2}>
          <ControllerTextField
            control={control}
            label="Confirm your password"
            name="confirmPassword"
            placeholder="Confirm Password"
            type="password"
          />
        </Grid>
        <Grid item mt={4} alignContent={'center'} justifyContent={'center'}>
          <T.TextSmallBold color={colors.neutral.shades.shade600}>
            Password must contain the following:
          </T.TextSmallBold>
          <T.TextSmallRegular mt={2} color={colors.neutral.shades.shade600}>
            <Box ml={4}>
              <ul>
                <li>At least 8 characters</li>
                <li>1 uppercase letter</li>
                <li>1 lowercase letter</li>
                <li>1 number</li>
                <li>1 special character</li>
              </ul>
            </Box>
          </T.TextSmallRegular>
        </Grid>

        <Grid item mt={2}>
          <Button
            type="submit"
            variant="blue"
            loading={isPending}
            disabled={isPending}
            mt={5}
          >
            {isPending ? 'Setting Password...' : 'Set Password'}
          </Button>
        </Grid>
      </form>
    </Grid>
  );
};

export default Register;
